import React from 'react'
import { classNames } from '../../utils/classNames'

export type LoaderVariant = 'spinner' | 'dots' | 'skeleton'
export type LoaderSize = 'sm' | 'md' | 'lg'

interface LoaderProps {
  variant?: LoaderVariant
  size?: LoaderSize
  fullPage?: boolean
  label?: string
  className?: string
}

const sizeMap = {
  sm: { spinner: 'h-6 w-6', dots: 'w-1.5 h-1.5' },
  md: { spinner: 'h-10 w-10', dots: 'w-2 h-2' },
  lg: { spinner: 'h-12 w-12', dots: 'w-2.5 h-2.5' }
}

/** Modern animated loader - ring spinner, bouncing dots, or skeleton placeholder */
export const Loader: React.FC<LoaderProps> = ({
  variant = 'spinner',
  size = 'md',
  fullPage = false,
  label,
  className = ''
}) => {
  const spinnerSize = sizeMap[size].spinner
  const dotsSize = sizeMap[size].dots

  const content = (
    <div className={classNames('flex flex-col items-center justify-center gap-3', className)}>
      {variant === 'spinner' && (
        <div
          className={classNames(
            'rounded-full border-2 border-neutral-200 dark:border-navy-600',
            'border-t-primary-500 dark:border-t-primary-400',
            'animate-[spin_0.8s_linear_infinite]',
            spinnerSize
          )}
          style={{ borderTopColor: 'var(--color-primary-500)' }}
        />
      )}
      {variant === 'dots' && (
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={classNames(
                'rounded-full bg-primary-500 dark:bg-primary-400',
                'animate-loader-bounce',
                dotsSize
              )}
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          ))}
        </div>
      )}
      {variant === 'skeleton' && (
        <div className="flex flex-col items-center gap-2 w-full max-w-[120px]">
          <div className="h-2 w-full rounded bg-neutral-200 dark:bg-navy-600 animate-pulse" />
          <div className="h-2 w-[75%] rounded bg-neutral-200 dark:bg-navy-600 animate-pulse" />
        </div>
      )}
      {label && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 font-medium">{label}</p>
      )}
    </div>
  )

  if (fullPage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-navy-900/80 backdrop-blur-sm">
        {content}
      </div>
    )
  }

  return content
}

/** Shimmer effect for skeleton blocks - use as child of elements with animate-pulse */
export const SkeletonBlock: React.FC<{
  className?: string
  height?: string
  width?: string
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full'
}> = ({ className = '', height, width, rounded = 'md' }) => {
  const roundedClass = {
    none: '',
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'
  }[rounded]
  return (
    <div
      className={classNames(
        'bg-neutral-200 dark:bg-navy-600 animate-pulse',
        'relative overflow-hidden',
        roundedClass,
        className
      )}
      style={{ height: height ?? '1rem', width: width ?? '100%' }}
    >
      <div
        className="absolute inset-0 -translate-x-full animate-shimmer"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
          width: '50%'
        }}
      />
    </div>
  )
}
